import React, { useState } from "react";
import css from "./ProductSlider3.module.css";
import ProductSliderComponent from "../ProductSliderComponent/ProductSliderComponent";
import { useOutletContext } from "react-router-dom";

interface Banner {
  title?: string;
  subtitle?: string;
  buttonText?: string;
  image?: string;
  url?: string;
  urlMetaTitle?: string; // Add urlMetaTitle property
}

interface ProductSlider3Props {
  title?: string;
  subtitle?: string;
  products: any[];
  viewAll?: boolean;
  url?: string;
  banner?: Banner;
}

const ProductSlider3: React.FC<ProductSlider3Props> = ({
  title,
  subtitle,
  products,
  viewAll,
  url,
  banner,
}) => {
  const { toggleMiniCart } = useOutletContext<{ toggleMiniCart: () => void }>();
  const [bannerLoaded, setBannerLoaded] = useState(false);

  // Add logging to check if banner exists
  console.log("Banner data received:", banner);

  // Add validation to ensure subtitle doesn't cause layout issues
  const formattedSubtitle =
    subtitle && typeof subtitle === "string"
      ? subtitle.length > 80
        ? `${subtitle.substring(0, 77)}...`
        : subtitle
      : "";

  if (!products || products.length === 0) return null;

  return (
    <div className={css.container}>
      <div className={css.sliderSection}>
        <div className={css.sliderHead}>
          <div className={css.header}>
            {title && <h2 className={css.title}>{title}</h2>}
            {viewAll && (
              <a
                href={url}
                className={css.viewAllLink}
                title={`Ver todos los productos de ${title}`}
                aria-label={`Ver todos los productos de ${title}`}
              >
                Ver todo
              </a>
            )}
          </div>
          {/* Use the validated/trimmed subtitle */}
          {formattedSubtitle && (
            <h3 className={css.subtitle}>{formattedSubtitle}</h3>
          )}
        </div>

        <div className={css.sliderContent}>
          <ProductSliderComponent
            products={products}
            sliderItems={3}
            toggleMiniCart={toggleMiniCart} // Pass the toggleMiniCart function directly
          />
        </div>
      </div>

      {banner && banner.image ? (
        <div
          className={css.bannerSection}
          data-banner-title={banner.title || "No Title"}
        >
          <div className={css.bannerContainer}>
            {banner.url ? (
              <a
                href={banner.url}
                title={banner.urlMetaTitle || banner.title || "Banner"}
                className={css.bannerLink}
              >
                <div className={css.banner}>
                  <img
                    src={banner.image}
                    alt={banner.title || "Product slider banner"}
                    title={banner.title || "Productos destacados"}
                    className={`${css.bannerImage} ${
                      bannerLoaded ? css.loaded : ""
                    }`}
                    aria-label={
                      banner.title || "Featured products promotional banner"
                    }
                    onLoad={() => setBannerLoaded(true)}
                    onError={(e) => {
                      console.error("Banner image failed to load:", e);
                      setBannerLoaded(true);
                    }}
                    loading="lazy"
                    width="100%"
                    height="100%"
                  />
                  <div className={css.bannerContent}>
                    {banner.title && (
                      <h3 className={css.bannerTitle}>{banner.title}</h3>
                    )}
                    {banner.subtitle && (
                      <p className={css.bannerSubtitle}>{banner.subtitle}</p>
                    )}
                    {banner.buttonText && (
                      <button className={css.bannerButton}>
                        {banner.buttonText}
                      </button>
                    )}
                  </div>
                </div>
              </a>
            ) : (
              <div className={css.banner}>
                <img
                  src={banner.image}
                  alt={banner.title || "Product slider banner"}
                  title={banner.title || "Productos destacados"}
                  className={`${css.bannerImage} ${
                    bannerLoaded ? css.loaded : ""
                  }`}
                  aria-label={
                    banner.title || "Featured products promotional banner"
                  }
                  onLoad={() => setBannerLoaded(true)}
                  onError={(e) => {
                    console.error("Banner image failed to load:", e);
                    setBannerLoaded(true);
                  }}
                  loading="lazy"
                  width="100%"
                  height="100%"
                />
                <div className={css.bannerContent}>
                  {banner.title && (
                    <h3 className={css.bannerTitle}>{banner.title}</h3>
                  )}
                  {banner.subtitle && (
                    <p className={css.bannerSubtitle}>{banner.subtitle}</p>
                  )}
                  {banner.buttonText && (
                    <button className={css.bannerButton}>
                      {banner.buttonText}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className={css.emptyBannerSection}>
          {/* Show placeholder or message when debugging */}
          {process.env.NODE_ENV === "development" && (
            <p>No banner data available</p>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductSlider3;
